/**
 * Locks a scroll gesture to one axis on containers that scroll both ways.
 *
 * The dashboard listings are wider than a phone and taller than the card, so
 * they scroll horizontally and vertically. Left alone the browser follows the
 * finger diagonally: a small curve while flicking down drags the names off the
 * left edge. Here the first few pixels of travel pick the axis and the other
 * one is frozen until the finger lifts, so the list moves up/down or
 * left/right but never both at once.
 *
 * Only containers that can scroll on both axes are taken over. Anything that
 * scrolls one way already behaves, and keeps native scrolling and momentum.
 * The footer legend used to be driven from here as well, held to the sideways
 * axis, but a hand-rolled drag never glides like the phone's own scroll and
 * the legend felt stuck; it scrolls natively now, and CSS (`touch-action:
 * pan-x` on .in-law-footer) keeps the up/down part of a swipe from moving the
 * page. pan-x also makes the browser refuse a swipe that sets off upwards, which
 * is how a thumb at the foot of the screen often starts, so only those swipes
 * are driven from here: a sideways start stays the phone's own.
 * The one addition for sideways-only strips is the mouse wheel: its vertical
 * turn scrolls them sideways, since they hide their scrollbar.
 */

const AXIS_COMMIT_PX = 6; // travel before a gesture commits to an axis
const FRICTION = 0.94; // velocity kept per 16ms frame after the finger lifts
const MIN_VELOCITY = 0.05; // px/ms at which momentum stops
const MAX_VELOCITY = 4; // px/ms cap so a hard flick stays readable
const WHEEL_IDLE_MS = 200; // gap in wheel events that ends a trackpad gesture

const scrolls = (overflow) => overflow === 'auto' || overflow === 'scroll';

function scrollAxes(el) {
  const style = window.getComputedStyle(el);
  const x = scrolls(style.overflowX) && el.scrollWidth - el.clientWidth > 1;
  const y = scrolls(style.overflowY) && el.scrollHeight - el.clientHeight > 1;
  return x || y ? { x, y } : null;
}

/**
 * The nearest scrollable ancestor is the one the browser would move, so that
 * is the only candidate. Returns it with the axes it can move on, or null.
 */
function findNearestScroller(node) {
  let el = node instanceof Element ? node : null;
  while (el && el !== document.body && el !== document.documentElement) {
    const axes = scrollAxes(el);
    if (axes) return { el, axes };
    el = el.parentElement;
  }
  return null;
}

/** The nearest scroller when it scrolls both ways, else null and the gesture
 *  is left to the browser. */
function findDualAxisScroller(node) {
  const found = findNearestScroller(node);
  return found && found.axes.x && found.axes.y ? found.el : null;
}

/** The nearest scroller when it scrolls sideways only — the footer legends
 *  and the sort-chip rows, which hide their scrollbar. */
function findHorizontalOnlyScroller(node) {
  const found = findNearestScroller(node);
  return found && found.axes.x && !found.axes.y ? found.el : null;
}

/** The footer legend when the touch lands on it and it has somewhere to go. */
function findLegendStrip(node) {
  const strip = findHorizontalOnlyScroller(node);
  return strip && strip.matches('.in-law-footer') ? strip : null;
}

const clampVelocity = (v) =>
  Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, v));

const readScroll = (el, axis) => (axis === 'x' ? el.scrollLeft : el.scrollTop);

function writeScroll(el, axis, value) {
  if (axis === 'x') el.scrollLeft = value;
  else el.scrollTop = value;
}

export function enableAxisLockScroll() {
  let target = null;
  let axis = null;
  let legendStrip = null; // touched legend, until the browser takes the swipe or not
  let legendOffered = false;
  let startX = 0;
  let startY = 0;
  let lastX = 0;
  let lastY = 0;
  let lastTime = 0;
  let velocity = 0;

  let momentumFrame = 0;

  const stopMomentum = () => {
    if (momentumFrame) cancelAnimationFrame(momentumFrame);
    momentumFrame = 0;
  };

  const startMomentum = (el, lockedAxis, initialVelocity) => {
    let v = initialVelocity;
    let prev = 0;
    const step = (now) => {
      const dt = prev ? Math.min(now - prev, 32) : 16;
      prev = now;
      const before = readScroll(el, lockedAxis);
      writeScroll(el, lockedAxis, before + v * dt);
      const moved = Math.abs(readScroll(el, lockedAxis) - before);
      v *= Math.pow(FRICTION, dt / 16);
      // moved === 0 means the list hit its end, so the fling is spent
      if (Math.abs(v) < MIN_VELOCITY || moved < 0.1) {
        stopMomentum();
        return;
      }
      momentumFrame = requestAnimationFrame(step);
    };
    momentumFrame = requestAnimationFrame(step);
  };

  const onTouchStart = (e) => {
    stopMomentum();
    target = null;
    legendStrip = null;
    if (e.touches.length !== 1) return;
    const dual = findDualAxisScroller(e.target);
    legendStrip = dual ? null : findLegendStrip(e.target);
    if (!dual && !legendStrip) return;
    legendOffered = false;
    const touch = e.touches[0];
    target = dual;
    axis = null;
    velocity = 0;
    startX = lastX = touch.clientX;
    startY = lastY = touch.clientY;
    lastTime = e.timeStamp;
  };

  const onTouchMove = (e) => {
    if (legendStrip) {
      if (e.touches.length !== 1) {
        legendStrip = null;
        return;
      }
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_COMMIT_PX) return;
      // An uncancelable move means the browser is scrolling; writing here too
      // would double the speed.
      if (!e.cancelable) {
        legendStrip = null;
        return;
      }
      // A sideways move gets one chance for the browser to pick it up. The
      // finger's direction alone can't settle it: the browser may already have
      // refused the swipe on moves that never reached this handler.
      if (Math.abs(dx) >= Math.abs(dy) && !legendOffered) {
        legendOffered = true;
        return;
      }
      target = legendStrip;
      axis = 'x';
      legendStrip = null;
    }

    if (!target) return;
    if (e.touches.length !== 1) {
      target = null;
      return;
    }
    // Cancel the native scroll on the very first move: once iOS starts
    // scrolling, preventDefault is ignored for the rest of the gesture.
    if (e.cancelable) e.preventDefault();

    const touch = e.touches[0];
    if (!axis) {
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_COMMIT_PX) return;
      axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }

    const moved = axis === 'x' ? touch.clientX - lastX : touch.clientY - lastY;
    const elapsed = Math.max(e.timeStamp - lastTime, 1);
    writeScroll(target, axis, readScroll(target, axis) - moved);
    // Smoothed so one jittery sample does not decide the fling speed
    velocity = clampVelocity(velocity * 0.3 + (-moved / elapsed) * 0.7);
    lastX = touch.clientX;
    lastY = touch.clientY;
    lastTime = e.timeStamp;
  };

  const onTouchEnd = (e) => {
    const el = target;
    const lockedAxis = axis;
    target = null;
    axis = null;
    legendStrip = null;
    if (!el || !lockedAxis) return;
    // A pause before lifting means the finger stopped, so do not fling
    if (e.timeStamp - lastTime > 100) return;
    if (Math.abs(velocity) > MIN_VELOCITY) startMomentum(el, lockedAxis, velocity);
  };

  const onTouchCancel = () => {
    target = null;
    axis = null;
    legendStrip = null;
  };

  let wheelTarget = null;
  let wheelAxis = null;
  let wheelIdle = 0;

  const onWheel = (e) => {
    // deltaMode: 0 px, 1 lines, 2 pages
    const wheelScale = (el) =>
      e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;

    const el = findDualAxisScroller(e.target);
    if (!el) {
      wheelTarget = null;
      wheelAxis = null;
      // A strip that scrolls sideways only (the footer legend, the sort chips)
      // hides its scrollbar, and a mouse wheel only turns up and down, so the
      // part off the right edge was unreachable without a trackpad. Steer the
      // vertical delta sideways; at either end the wheel falls through to the
      // page as usual.
      const strip = findHorizontalOnlyScroller(e.target);
      if (!strip || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const before = strip.scrollLeft;
      strip.scrollLeft = before + e.deltaY * wheelScale(strip);
      if (strip.scrollLeft !== before && e.cancelable) e.preventDefault();
      return;
    }
    const scale = wheelScale(el);
    const dx = e.deltaX * scale;
    const dy = e.deltaY * scale;
    if (el !== wheelTarget || wheelAxis === null) {
      wheelTarget = el;
      wheelAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    clearTimeout(wheelIdle);
    wheelIdle = setTimeout(() => {
      wheelTarget = null;
      wheelAxis = null;
    }, WHEEL_IDLE_MS);

    const delta = wheelAxis === 'x' ? dx : dy;
    const before = readScroll(el, wheelAxis);
    writeScroll(el, wheelAxis, before + delta);
    // At the end of the list, hand the gesture back so parents can scroll
    if (readScroll(el, wheelAxis) !== before && e.cancelable) e.preventDefault();
  };

  document.addEventListener('touchstart', onTouchStart, { passive: true });
  document.addEventListener('touchmove', onTouchMove, { passive: false });
  document.addEventListener('touchend', onTouchEnd, { passive: true });
  document.addEventListener('touchcancel', onTouchCancel, { passive: true });
  document.addEventListener('wheel', onWheel, { passive: false });

  return () => {
    stopMomentum();
    clearTimeout(wheelIdle);
    document.removeEventListener('touchstart', onTouchStart);
    document.removeEventListener('touchmove', onTouchMove);
    document.removeEventListener('touchend', onTouchEnd);
    document.removeEventListener('touchcancel', onTouchCancel);
    document.removeEventListener('wheel', onWheel);
  };
}

export default enableAxisLockScroll;
